import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/XhsProfileEngagementInsights.graphql';
import { Query, QueryxhsProfileEngagementInsightsArgs } from '../../__generated__/globalTypes';

export const useXhsProfileEngagementInsightsQuery = (
  options?: QueryHookOptions<Query, QueryxhsProfileEngagementInsightsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryxhsProfileEngagementInsightsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useXhsProfileEngagementInsightsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryxhsProfileEngagementInsightsArgs>
): QueryResult<Query, QueryxhsProfileEngagementInsightsArgs> & {
  getXhsProfileEngagementInsights: LazyQueryExecFunction<Query, QueryxhsProfileEngagementInsightsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getXhsProfileEngagementInsights, result] = useLazyQuery<Query, QueryxhsProfileEngagementInsightsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getXhsProfileEngagementInsights, ...result };
};

export const useXhsProfileEngagementInsightsPromiseQuery = () => {
  const client = useApolloClient();

  const getXhsProfileEngagementInsights = useCallback(
    (options?: Omit<QueryOptions<QueryxhsProfileEngagementInsightsArgs, Query>, 'query'>) =>
      client.query<Query, QueryxhsProfileEngagementInsightsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getXhsProfileEngagementInsights };
};
// Suspense Query Hooks
export const useXhsProfileEngagementInsightsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryxhsProfileEngagementInsightsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryxhsProfileEngagementInsightsArgs>(QUERY, {
    ...options
  });

  return result;
};
